import { cn } from '@/app/lib/utils';
import { GameType } from '@puzzlio/core';
import { useBackgroundBurstPosition } from '@/app/hooks/useBackgroundBurstPosition';
import { GAME_META } from '@puzzlio/games';
import { BURST } from '@puzzlio/ui/spec/burst';
import { PUZZLE_GRID_SELECTOR } from '@puzzlio/render';

export const BackgroundBurst = ({
  color1,
  color2,
  className,
  anchorSelector,
  isVisible,
}: {
  color1: string;
  color2: string;
  className?: string;
  /** Centre the burst on the first sized element this matches; unset leaves it where it sits. */
  anchorSelector?: string;
  isVisible: boolean;
}) => {
  const { position, burstRef } = useBackgroundBurstPosition(anchorSelector, isVisible);

  return (
    <svg
      ref={burstRef}
      viewBox={BURST.viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(
        'pointer-events-none absolute scale-0',
        className,
        isVisible && 'scale-100 transition-transform duration-1000 ease-out'
      )}
      style={
        position
          ? {
              left: `${position.x}px`,
              top: `${position.y}px`,
              width: `${position.width}px`,
              height: `${position.height}px`,
            }
          : {}
      }
    >
      <path d={BURST.path} fill="url(#paint0_radial_0_20)" />
      <defs>
        <radialGradient
          id="paint0_radial_0_20"
          cx={BURST.gradient.cx}
          cy={BURST.gradient.cy}
          r={BURST.gradient.r}
          gradientUnits="userSpaceOnUse"
        >
          {BURST.stops.map((stop, index) => (
            <stop
              key={index}
              offset={stop.offset}
              stopColor={stop.color === 'inner' ? color2 : color1}
              stopOpacity={'opacity' in stop ? stop.opacity : undefined}
            />
          ))}
        </radialGradient>
      </defs>
    </svg>
  );
};

export const GameBackgroundBurst = ({
  game,
  className,
  isVisible,
}: {
  game: GameType;
  className?: string;
  isVisible: boolean;
}) => {
  // `color2` is the bright middle and `color1` the colour the rays fade through — see BURST's stops.
  const { inner: color2, outer: color1 } = GAME_META[game].burst;
  return (
    <BackgroundBurst
      color1={color1}
      color2={color2}
      anchorSelector={PUZZLE_GRID_SELECTOR}
      isVisible={isVisible}
      className={className}
    />
  );
};
